// Named tile bag variants — single source of truth for client, worker, and agent.
// Every variant is exactly 72 tiles (including blanks) so game length stays consistent.
// Blanks are keyed as ' ' (space); consumers set isBlank via letter === ' '.
//
// Distributions are tuned against SOWPODS letter frequency for playable words
// (2-8 letters), then deliberately skewed per variant for personality.

export const TILE_POINTS = {
    'E': 1, 'T': 1, 'A': 1, 'O': 1, 'I': 1, 'N': 1, 'S': 1, 'R': 1,
    'H': 2, 'L': 2, 'D': 2,
    'C': 3, 'U': 3, 'M': 3, 'W': 3, 'F': 3, 'G': 3, 'Y': 3, 'P': 3,
    'B': 5, 'V': 5, 'K': 5,
    'J': 8, 'X': 8,
    'Q': 12, 'Z': 12,
    ' ': 0
};

export const TILE_BAG_VARIANTS = {
    houseBlend: {
        name: 'House Blend',
        tagline: 'The standard mix — a little of everything',
        distribution: {
            'E': 9, 'T': 4, 'A': 6, 'O': 5, 'I': 6, 'N': 4, 'S': 4, 'R': 4,
            'H': 2, 'L': 3, 'D': 3,
            'C': 2, 'U': 3, 'M': 2, 'W': 1, 'F': 1, 'G': 2, 'Y': 1, 'P': 1,
            'B': 1, 'V': 1, 'K': 1,
            'J': 1, 'X': 1,
            'Q': 1, 'Z': 1,
            ' ': 2
        }
    },
    vowelPlay: {
        name: 'Vowel Play',
        tagline: 'Vowel-rich and flowy — easy hooks, long chains',
        distribution: {
            'E': 10, 'T': 3, 'A': 7, 'O': 5, 'I': 7, 'N': 3, 'S': 4, 'R': 4,
            'H': 2, 'L': 3, 'D': 2,
            'C': 2, 'U': 3, 'M': 2, 'W': 1, 'F': 1, 'G': 2, 'Y': 1, 'P': 1,
            'B': 1, 'V': 1, 'K': 1,
            'J': 1, 'X': 1,
            'Q': 1, 'Z': 1,
            ' ': 2
        }
    },
    consonantCrunch: {
        name: 'Consonant Crunch',
        tagline: 'Gritty racks — earn every vowel',
        distribution: {
            'E': 8, 'T': 4, 'A': 5, 'O': 4, 'I': 5, 'N': 5, 'S': 4, 'R': 5,
            'H': 2, 'L': 4, 'D': 3,
            'C': 2, 'U': 3, 'M': 2, 'W': 1, 'F': 1, 'G': 2, 'Y': 1, 'P': 2,
            'B': 1, 'V': 1, 'K': 1,
            'J': 1, 'X': 1,
            'Q': 1, 'Z': 1,
            ' ': 2
        }
    },
    spiceRack: {
        name: 'Spice Rack',
        tagline: 'Double the danger tiles — big swings',
        distribution: {
            'E': 8, 'T': 3, 'A': 5, 'O': 4, 'I': 5, 'N': 3, 'S': 3, 'R': 4,
            'H': 2, 'L': 2, 'D': 2,
            'C': 2, 'U': 4, 'M': 2, 'W': 1, 'F': 1, 'G': 2, 'Y': 1, 'P': 1,
            'B': 2, 'V': 2, 'K': 2,
            'J': 2, 'X': 2,
            'Q': 2, 'Z': 2,
            ' ': 3
        }
    },
    decaf: {
        name: 'Decaf',
        tagline: 'No Q, no Z, no stress — smooth sailing',
        distribution: {
            'E': 9, 'T': 4, 'A': 6, 'O': 5, 'I': 6, 'N': 4, 'S': 4, 'R': 5,
            'H': 2, 'L': 4, 'D': 3,
            'C': 2, 'U': 3, 'M': 2, 'W': 1, 'F': 1, 'G': 2, 'Y': 1, 'P': 2,
            'B': 1, 'V': 1, 'K': 1,
            ' ': 3
        }
    },
    steve: {
        name: 'Steve',
        tagline: 'Extra S, T, E and V — classic Steve',
        distribution: {
            'E': 12, 'T': 6, 'A': 5, 'O': 4, 'I': 5, 'N': 3, 'S': 6, 'R': 3,
            'H': 2, 'L': 2, 'D': 2,
            'C': 2, 'U': 3, 'M': 1, 'W': 1, 'F': 1, 'G': 1, 'Y': 1, 'P': 1,
            'B': 1, 'V': 3, 'K': 1,
            'J': 1, 'X': 1,
            'Q': 1, 'Z': 1,
            ' ': 2
        }
    },
    carl: {
        name: 'Carl',
        tagline: 'Heavy on C, A, R and L — Carl insists',
        distribution: {
            'E': 8, 'T': 3, 'A': 9, 'O': 4, 'I': 5, 'N': 3, 'S': 3, 'R': 6,
            'H': 2, 'L': 5, 'D': 2,
            'C': 4, 'U': 3, 'M': 1, 'W': 1, 'F': 1, 'G': 1, 'Y': 1, 'P': 1,
            'B': 1, 'V': 1, 'K': 1,
            'J': 1, 'X': 1,
            'Q': 1, 'Z': 1,
            ' ': 2
        }
    }
};

export const DEFAULT_VARIANT_KEY = 'houseBlend';

export const VARIANT_KEYS = Object.keys(TILE_BAG_VARIANTS);

export function isVariantKey(key) {
    return Object.prototype.hasOwnProperty.call(TILE_BAG_VARIANTS, key);
}

// Always returns a variant — falls back to the default for unknown/legacy keys
export function getVariant(key) {
    return TILE_BAG_VARIANTS[key] || TILE_BAG_VARIANTS[DEFAULT_VARIANT_KEY];
}

export function pickRandomVariantKey() {
    return VARIANT_KEYS[Math.floor(Math.random() * VARIANT_KEYS.length)];
}
